export const electronicsPageData = {
  stats: [
    { value: "35%", label: "Procurement Cost Reduction" },
    { value: "60%", label: "Risk Incident Reduction" },
    { value: "5×", label: "Faster Compliance Audits" },
    { value: "100%", label: "Supply Chain Visibility" },
  ],
  statsAccent: "from-violet-600 to-indigo-700",

  challengeTitle: "Electronics Procurement Is One of the World's Most Complex Supply Chains",
  challengeSubtitle:
    "Semiconductor shortages, geopolitical risk, regulatory complexity, and component volatility make electronics procurement uniquely challenging.",
  challenges: [
    {
      icon: "🌍",
      title: "Geopolitical & Supplier Risk",
      description:
        "Single-source dependencies and concentrated supply chains expose electronics companies to catastrophic disruptions.",
    },
    {
      icon: "💸",
      title: "Component Price Volatility",
      description:
        "Rapid price fluctuations in semiconductors and components make budget control nearly impossible without real-time data.",
    },
    {
      icon: "📋",
      title: "RoHS & REACH Compliance Burden",
      description:
        "Navigating RoHS, REACH, WEEE, and conflict minerals regulations across multiple markets is resource-intensive and error-prone.",
    },
    {
      icon: "⚠️",
      title: "Semiconductor Shortage Vulnerability",
      description:
        "Without early warning systems, component shortages halt production lines — costing millions in lost output.",
    },
    {
      icon: "🌿",
      title: "Conflict Minerals & ESG Obligations",
      description:
        "Proving ethical sourcing and meeting ESG commitments requires supply chain traceability that most companies lack.",
    },
    {
      icon: "🗂️",
      title: "Fragmented Data Silos",
      description:
        "Disconnected ERPs, procurement tools, and supplier portals create blind spots that slow critical decisions.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-violet-50 text-violet-700 border-violet-100",
    solutionIconBg: "bg-violet-50",
    solutionIconColor: "text-violet-600",
    solutionAccent: "from-violet-600 to-indigo-600",
  },

  solutionTitle: "AI-Powered Electronics Procurement That Handles Every Complexity",
  solutionSubtitle:
    "Predictive risk management, automated compliance, and unified supply chain intelligence — built for electronics precision.",
  solutions: [
    {
      icon: "🚨",
      title: "Predictive Supplier Risk Management",
      description:
        "AI continuously monitors supplier health, geopolitical events, and delivery performance — identifying risks weeks before they disrupt production.",
    },
    {
      icon: "💰",
      title: "Dynamic Budget Optimisation",
      description:
        "Real-time spend analytics and AI forecasting help you navigate component price volatility and stay within cost targets.",
    },
    {
      icon: "✅",
      title: "Automated Compliance Intelligence",
      description:
        "Continuous RoHS, REACH, WEEE, and conflict minerals monitoring with automatic alerts and audit-ready reports.",
    },
    {
      icon: "🔄",
      title: "Supply Chain Resilience Engine",
      description:
        "AI maintains an always-updated alternative supplier database — enabling rapid response to component shortages.",
    },
    {
      icon: "🌿",
      title: "Conflict Mineral Traceability",
      description:
        "End-to-end supply chain tracing verifies ethical sourcing and generates automated ESG compliance documentation.",
    },
    {
      icon: "📊",
      title: "Unified Procurement Intelligence",
      description:
        "All procurement data — from every tool, ERP, and supplier — consolidated into one AI-powered intelligence platform.",
    },
    {
      icon: "🌐",
      title: "Supplier Diversification Tools",
      description:
        "Discover and qualify alternative suppliers across geographies to reduce single-source risk and build negotiating leverage.",
    },
    {
      icon: "⚡",
      title: "Intelligent Process Automation",
      description:
        "Automate RFQs, PO creation, approval workflows, and invoice processing — freeing procurement teams for strategic work.",
    },
    {
      icon: "🔮",
      title: "Demand-Driven Early Procurement",
      description:
        "Demand forecasting identifies upcoming shortages months in advance — enabling early procurement before prices spike.",
    },
  ],

  howItWorksTitle: "Resilient Electronics Procurement in 4 Steps",
  howItWorksSteps: [
    {
      icon: "🔗",
      title: "Integrate All Data Sources",
      description: "Connect your ERP, supplier portals, and procurement tools — AI unifies everything in one view.",
    },
    {
      icon: "🤖",
      title: "AI Monitors Continuously",
      description: "Risk signals, compliance status, and pricing changes are tracked 24/7 — automatically.",
    },
    {
      icon: "🚨",
      title: "Risks Flagged Proactively",
      description: "AI surfaces supply chain risks before they materialise and recommends mitigation strategies.",
    },
    {
      icon: "📈",
      title: "Costs and Risk Decline",
      description: "Monitor procurement cost savings, risk reduction, and compliance improvements in real time.",
    },
  ],

  outcomeTitle: "Electronics Supply Chain Excellence Delivered",
  outcomeSubtitle: "Measurable outcomes for electronics companies using EkkBaz AI.",
  outcomes: [
    {
      value: "35%",
      metric: "Procurement Cost Reduction",
      description:
        "AI-powered spend analytics, supplier benchmarking, and demand forecasting deliver 35% average cost reduction within 90 days.",
    },
    {
      value: "60%",
      metric: "Risk Incident Reduction",
      description:
        "Predictive supply chain monitoring and alternative supplier networks reduce disruption incidents by 60%.",
    },
    {
      value: "5×",
      metric: "Faster Compliance Audits",
      description:
        "Automated compliance documentation and real-time supplier certification tracking make audits 5× faster.",
    },
  ],
  outcomeImage: "/assets/images/electronics_solution.jpeg",

  ctaTitle: "Build an Electronics Supply Chain That Can Handle Anything",
  ctaSubtitle:
    "Book a consultation to see how EkkBaz AI can reduce your procurement costs by 35% and cut supply chain risk by 60%.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const electronicsFacilities = [
  {
    id: 1,
    extraLabel: "Your Challenges",
    userType: "The Procurement Challenges Holding Electronics Businesses Back",
    description: {
      line1: `<strong>Supplier Reliability and Geopolitical Risk</strong> — Global supply chain disruptions and single-source dependencies create critical vulnerabilities in electronics procurement.`,
      line2: `<strong>Cost Volatility and Budget Overruns</strong> — Rapid component price fluctuations and opaque supplier pricing make it difficult to control procurement costs.`,
      line3: `<strong>Regulatory and RoHS Compliance</strong> — Navigating RoHS, REACH, WEEE, and other electronics-specific regulations across multiple markets is complex and resource-intensive.`,
      line4: `<strong>Supply Chain Disruptions</strong> — Semiconductor shortages and logistics delays can halt production lines, causing millions in lost revenue.`,
      line5: `<strong>Sustainability and Ethical Sourcing</strong> — Meeting conflict mineral regulations and ESG commitments requires traceability that most companies lack.`,
    },
    banner: "/assets/images/electronics_problem.png",
  },
  {
    id: 2,
    extraLabel: "Our Solutions",
    userType: "AI-Powered Procurement Built for Electronics Complexity",
    description: {
      line1: `<strong>Predictive Supplier Risk Management</strong> — AI continuously monitors supplier health, geopolitical risk, and delivery performance to identify risks before they impact your supply chain.`,
      line2: `<strong>Dynamic Budget Optimization</strong> — Real-time spend analytics and AI-driven budget forecasting help you stay within cost targets and identify savings opportunities.`,
      line3: `<strong>Automated Compliance Intelligence</strong> — Continuous regulatory monitoring with automatic alerts ensures your supply chain stays compliant with RoHS, REACH, and other mandates.`,
      line4: `<strong>Supply Chain Resilience Engine</strong> — AI identifies alternative suppliers and recommends contingency strategies to minimize disruption from component shortages.`,
      line5: `<strong>Conflict Mineral and ESG Traceability</strong> — End-to-end supply chain tracing to verify ethical sourcing and generate compliance reports automatically.`,
    },
    banner: "/assets/images/electronics_solution.jpeg",
  },
  {
    id: 3,
    extraLabel: "Get Started",
    userType: "Cut Electronics Procurement Costs by 35% with AI",
    description: `Electronics companies using EkkBaz AI have reduced procurement costs by 35%, cut supply chain disruption incidents by 60%, and achieved full regulatory compliance — all within the first 90 days.`,
    btnLabel: "Book a Free Consultation",
    formLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
    banner: "/assets/images/medicine3.png",
  },
];

export const electronicsFAQs = [
  {
    question: "How does EkkBaz AI help electronics companies manage supply chain disruptions?",
    answer:
      "EkkBaz AI monitors your entire supply chain in real time, uses predictive analytics to identify disruption risks, and automatically recommends alternative suppliers and contingency procurement strategies.",
  },
  {
    question: "Does EkkBaz AI support RoHS and REACH compliance management?",
    answer:
      "Yes. EkkBaz AI continuously monitors supplier compliance documentation for RoHS, REACH, WEEE, and other electronics-specific regulations, automatically flagging risks and generating compliance reports for audits.",
  },
  {
    question: "How does EkkBaz AI help with semiconductor shortage management?",
    answer:
      "EkkBaz AI maintains an always-updated database of alternative component suppliers and uses demand forecasting to recommend early procurement actions before shortages materialise.",
  },
];
